import styled from "@emotion/styled";
import { css } from "@emotion/react";
import { physioMeMobileBreakpoint, physioMeTabletBreakpoint } from "@ui/style";
import { Dialog, DialogProps } from "primereact/dialog";
import React, { PropsWithChildren } from "react";

export enum DIALOG_SIZE {
	S,
	M,
	L,
	XL,
}

export interface BaseDialogProps extends DialogProps {
	width?: DIALOG_SIZE;
	height?: string;
	tabletSameAsMobile?: boolean;
	hideScrollbar?: boolean;
}

const getWidth = (width?: DIALOG_SIZE) => {
	switch (width) {
		case DIALOG_SIZE.S:
			return "480px";
		case DIALOG_SIZE.M:
			return "684px";
		case DIALOG_SIZE.L:
			return "732px";
		case DIALOG_SIZE.XL:
			return `${window.innerWidth - 300}px`;
		default:
			return "100%";
	}
};

export const BaseDialog = React.forwardRef<Dialog, PropsWithChildren<BaseDialogProps>>(
	(
		{
			children,
			resizable = false,
			draggable = false,
			blockScroll = true,
			footer = <></>,
			width,
			height,
			tabletSameAsMobile = false,
			hideScrollbar = false,
			dismissableMask = true,
			...otherProps
		},
		ref,
	) => (
		<StyledDialog
			{...otherProps}
			ref={ref}
			resizable={resizable}
			draggable={draggable}
			blockScroll={blockScroll}
			footer={footer}
			width={width}
			height={height}
			tabletSameAsMobile={tabletSameAsMobile}
			hideScrollbar={hideScrollbar}
			dismissableMask={dismissableMask}
		>
			{children}
		</StyledDialog>
	),
);
BaseDialog.displayName = "BaseDialog";

const mobileStyles = css`
	max-height: 100%;
	width: 100%;
	height: 100%;
	max-width: initial;

	.p-dialog-header {
		flex-direction: row-reverse;
		gap: 16px;

		::after {
			left: 16px;
			right: 16px;
		}
	}

	.p-dialog-header,
	.p-dialog-footer,
	.p-dialog-content {
		border-radius: 0;
		padding: 16px;
	}

	.p-dialog-content {
		display: flex;
		flex-direction: column;
		height: 100%;
	}
`;

const StyledDialog = styled(Dialog)<{
	width?: DIALOG_SIZE;
	height?: string;
	tabletSameAsMobile: boolean;
	hideScrollbar?: boolean;
}>`
	max-width: ${() => getWidth(DIALOG_SIZE.XL)};
	${({ height }) => height && `min-height: ${height};`}
	width: ${({ width }) => getWidth(width)};

	.p-dialog-content {
		overflow: ${({ hideScrollbar }) => (hideScrollbar ? "hidden" : "auto")};
	}

	.p-dialog-footer:empty {
		padding-top: 0;
		padding-bottom: 1rem;
	}

	@media ${physioMeMobileBreakpoint} {
		${mobileStyles}
	}

	${({ tabletSameAsMobile }) =>
		tabletSameAsMobile &&
		css`
			@media ${physioMeTabletBreakpoint} {
				${mobileStyles}
			}
		`}
`;
